import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useSaveBillingsOverride, useUsers } from '@/hooks/use-api'
import { explainError } from '@/lib/explain-error'

interface ReattributeLineProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  line: {
    line_hash: string
    customer_name: string | null
    product_service: string | null
    txn_date: string
    doc_number: string | null
    raw_rep: string | null
    amount: number
    identical_lines: number
    override_source?: string | null
    override_reason?: string | null
  } | null
}

/**
 * Move a billing line to a different rep.
 *
 * This is the replacement for the "Corrected Sales Rep" column, and it is the main
 * thing anyone does with this data. Measured on Jason's workbook: of 1,985
 * corrections, 1,953 were on lines whose rep resolved perfectly well — they were
 * simply attributed to the wrong person. Only 32 involved a blank rep. So the
 * Attribution queue, which lists names the CRM could not place, covers under 2% of
 * the real work; this dialog covers the rest.
 *
 * Two things it must say out loud:
 *
 *  - how many lines the decision moves. An override keys on the content of a line,
 *    so identical lines share one decision — on the real data one content key
 *    occurs 317 times. Changing "this line" can move hundreds.
 *  - that the decision survives the next upload. That is the whole point of
 *    recording it here rather than in a spreadsheet, and it is not obvious.
 */
export function ReattributeLine({ open, onOpenChange, line }: ReattributeLineProps) {
  const { toast } = useToast()
  const users = useUsers({ limit: 10000 })
  const save = useSaveBillingsOverride()

  const [choice, setChoice] = useState('')
  const [isHouse, setIsHouse] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')

  const userList = users.data?.data ?? []
  const money = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 })

  const handleSave = async () => {
    if (!line || !choice) return
    setError('')
    try {
      const isUser = choice.startsWith('005')
      const result = await save.mutateAsync({
        line_hash: line.line_hash,
        rep_user_id: isUser ? choice : null,
        rep_label: isUser ? undefined : choice,
        is_house: isHouse,
        reason: reason || undefined,
      })
      toast({
        title: 'Line reattributed',
        description:
          result.lines_affected === 1
            ? 'One line moved. It will stay this way after the next upload.'
            : `${result.lines_affected.toLocaleString()} identical lines moved. They will stay this way after the next upload.`,
      })
      setChoice('')
      setIsHouse(false)
      setReason('')
      onOpenChange(false)
    } catch (err) {
      setError(explainError(err, 'Could not reattribute that line.'))
    }
  }

  if (!line) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="reattribute-desc">
        <DialogHeader>
          <DialogTitle>Reattribute this line</DialogTitle>
          <DialogDescription id="reattribute-desc">
            Move these billings to a different rep. The change is kept when new data is uploaded.
          </DialogDescription>
        </DialogHeader>

        <dl className="text-sm space-y-1">
          <div className="flex gap-2">
            <dt className="text-muted-foreground min-w-[6rem]">Customer</dt>
            <dd>{line.customer_name ?? '—'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground min-w-[6rem]">Product</dt>
            <dd>{line.product_service ?? '—'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground min-w-[6rem]">Date</dt>
            <dd>
              {formatDate(line.txn_date)}
              {line.doc_number ? ` · invoice ${line.doc_number}` : ''}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground min-w-[6rem]">Amount</dt>
            <dd style={{ fontVariantNumeric: 'tabular-nums' }}>{money(line.amount)}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground min-w-[6rem]">In QuickBooks</dt>
            <dd>{line.raw_rep?.trim() ? line.raw_rep : '(blank)'}</dd>
          </div>
        </dl>

        {/* Said plainly, because "this line" can be hundreds of lines. */}
        {line.identical_lines > 1 && (
          <Alert role="note">
            <AlertDescription>
              This will move{' '}
              <strong>{line.identical_lines.toLocaleString()} identical lines</strong> — same
              customer, product, date, invoice and amount. They cannot be split.
            </AlertDescription>
          </Alert>
        )}

        {line.override_reason && (
          <Alert role="note">
            <AlertDescription>Currently set manually: {line.override_reason}</AlertDescription>
          </Alert>
        )}

        {line.override_source === 'workbook' && (
          <Alert role="note">
            <AlertDescription>
              This line was last set by the Sales Tracker workbook. Changing it here means future
              workbook uploads will no longer overwrite it.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <div>
            <Label htmlFor="reattribute-to">Attribute to</Label>
            <select
              id="reattribute-to"
              className="mt-1 block w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
            >
              <option value="">Choose…</option>
              <optgroup label="People">
                {userList.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Not a person">
                <option value="Unassigned">Unassigned</option>
                <option value="GrackleDocs">GrackleDocs</option>
              </optgroup>
            </select>
          </div>

          {/* House is its own dimension, not part of the name: his corrections
              distinguish "Shena Walker" from "Shena Walker - House" 182 times. */}
          <div className="flex items-start gap-2">
            <Checkbox
              id="reattribute-house"
              checked={isHouse}
              onCheckedChange={(v) => setIsHouse(v === true)}
              className="mt-0.5"
            />
            <Label htmlFor="reattribute-house" className="font-normal cursor-pointer">
              House account
              <span className="block text-xs text-muted-foreground">
                Counts toward the rep's total, and is shown separately
              </span>
            </Label>
          </div>

          <div>
            <Label htmlFor="reattribute-reason">Why (optional)</Label>
            <Input
              id="reattribute-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. house account, or transferred mid-term"
              aria-describedby="reattribute-reason-help"
            />
            <p id="reattribute-reason-help" className="text-xs text-muted-foreground mt-1">
              Worth a note — this is a judgement, and the reason is what makes it reviewable later.
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription role="alert">{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={save.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!choice || save.isPending}
            aria-label="Save this reattribution"
          >
            {save.isPending ? 'Saving' : 'Reattribute'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
